import { lazy } from "react";
import SenderRegistrationsPage from "../pages/sender-registrations";
import ContactCenterPage from "../pages/contact-center";
import CallRoutingPage from "../pages/call-routing";
import CallRecordingPage from "../pages/call-recording";
import LogsPage from "../pages/logs";
const ContactCenterSetup = lazy(() => import("../pages/contact-center/setup"));
const OnboardingFlow = lazy(() => import("src/pages/onboarding"));
const AccountSelector = lazy(
  () => import("src/pages/onboarding/AccountSelector"),
);
const PaymentStatusPage = lazy(
  () => import("src/pages/onboarding/payment-status"),
);
const EnterpriseProcessingPage = lazy(
  () => import("src/pages/onboarding/enterprise-processing"),
);
const AddChannel = lazy(() => import("src/pages/channels/create/add-channel"));
const UpdateChannel = lazy(
  () => import("src/pages/channels/update/update-channel"),
);
const AccountSettings = lazy(() => import("src/pages/account/my-settins"));
const BillingPage = lazy(() => import("src/pages/account/billing"));
const PaymentProcessingPage = lazy(
  () => import("src/pages/account/payment-processing"),
);
const OverageConsumptionPage = lazy(
  () => import("src/pages/billing/overage-consumption"),
);
const StudioPage = lazy(() => import("src/pages/studio/StudioPage"));
const ShadowCanvasPage = lazy(
  () => import("src/pages/shadow-canvas/ShadowCanvasPage"),
);
const UserListPage = lazy(() => import("src/pages/users/list/list"));
const RolesListPage = lazy(() => import("src/pages/roles/list/list"));
const AddRole = lazy(() => import("src/pages/roles/create/add-role"));
const WorkflowsListPage = lazy(() => import("src/pages/workflows"));
const CreateWorkflowPage = lazy(
  () => import("src/pages/workflows/create-workflow"),
);
const Channels = lazy(() => import("src/pages/channels/list"));
const ChannelDetailPage = lazy(() => import("src/pages/channels/detail"));
const WebchatPreviewPage = lazy(
  () => import("src/pages/channels/webchat/WebchatPreview"),
);
const CavePage = lazy(() => import("src/pages/my-cave"));
const DashboardPage = lazy(() => import("src/pages/analytics"));
const ConversationsPage = lazy(() => import("src/pages/conversations"));
const ConversationDetailPage = lazy(
  () => import("src/pages/conversations/[id]"),
);
const TicketsPage = lazy(() => import("src/pages/tickets"));
const TicketDetailPage = lazy(() => import("src/pages/tickets/[id]"));
const CampaignsPage = lazy(() => import("src/pages/campaigns"));
const CreateCampaignPage = lazy(() => import("src/pages/campaigns/create"));
const EditCampaignPage = lazy(() => import("src/pages/campaigns/[id]/edit"));
const CampaignDetailPage = lazy(() => import("src/pages/campaigns/[id]"));
const TemplatesPage = lazy(() => import("src/pages/templates"));
const CreateTemplatePage = lazy(() => import("src/pages/templates/create"));
const TemplateDetailPage = lazy(() => import("src/pages/templates/[id]"));
const TemplateEditPage = lazy(() => import("src/pages/templates/[id]/edit"));
const ChatbotsPage = lazy(() => import("src/pages/chatbots"));
const CreateChatbotPage = lazy(() => import("src/pages/chatbots/create"));
const ChatbotViewPage = lazy(() => import("src/pages/chatbots/[id]"));
const EditChatbotPage = lazy(() => import("src/pages/chatbots/[id]/edit"));
const SocialListeningPage = lazy(() => import("src/pages/social-listening"));
const StripeTestPage = lazy(() => import("src/pages/stripe-test"));
const CDPPage = lazy(() => import("src/pages/cdp"));
const ProfileDetailsPage = lazy(() => import("src/pages/cdp/profile/[id]"));
// Customers pages
const CustomerDashboardPage = lazy(
  () => import("src/pages/customers/dashboard"),
);
const PeoplePage = lazy(() => import("src/pages/customers/people"));
const SegmentsPage = lazy(() => import("src/pages/customers/segments"));
const EventsPage = lazy(() => import("src/pages/customers/events"));
const LabelsPage = lazy(() => import("src/pages/customers/labels"));
const ConsentPage = lazy(() => import("src/pages/customers/consent"));
const RulesPage = lazy(() => import("src/pages/customers/rules"));
const RuleDetailPage = lazy(() => import("src/pages/customers/rules/detail"));
const DefinitionsPage = lazy(() => import("src/pages/customers/definitions"));
const GoalsPage = lazy(() => import("src/pages/customers/goals"));
const PropertiesPage = lazy(() => import("src/pages/customers/properties"));
const PersonasPage = lazy(() => import("src/pages/customers/personas"));
const ListsPage = lazy(() => import("src/pages/customers/lists"));
const AiActionsPage = lazy(() => import("src/pages/customers/actions"));
const ScoringPage = lazy(() => import("src/pages/customers/scoring"));
const FilesPage = lazy(() => import("src/pages/files"));
const IntegrationsPage = lazy(() => import("src/pages/integrations"));

// Sender Registration Pages
const WhatsAppRegisterPage = lazy(
  () => import("src/pages/sender-registrations/whatsapp/register"),
);
const SMSRegisterPage = lazy(
  () => import("src/pages/sender-registrations/sms/register"),
);
const WhatsAppDetailsPage = lazy(
  () => import("src/pages/sender-registrations/whatsapp/details"),
);
const SMSDetailsPage = lazy(
  () => import("src/pages/sender-registrations/sms/details"),
);

// Meta Partner Onboarding Pages
const MetaPartnerOnboardingPage = lazy(
  () => import("src/pages/meta-partner/onboarding"),
);
const MetaPartnerListPage = lazy(() => import("src/pages/meta-partner/list"));

// Support Center / Help Center Pages
const HelpCenterPage = lazy(
  () => import("src/pages/support-center/help-center"),
);
const SupportTicketsPage = lazy(
  () => import("src/pages/support-center/tickets"),
);
const SupportTicketDetailPage = lazy(
  () => import("src/pages/support-center/tickets/[id]"),
);
const NewSupportTicketPage = lazy(
  () => import("src/pages/support-center/tickets/new"),
);

// WhatsApp Onboarding Public Pages
const WhatsAppOnboardingList = lazy(
  () => import("src/pages/whatsapp-onboarding/list"),
);
const WhatsAppOnboardingFlow = lazy(
  () => import("src/pages/whatsapp-onboarding"),
);
const WhatsAppOnboardingDetail = lazy(
  () => import("src/pages/whatsapp-onboarding/detail"),
);
const WhatsAppOnboardingSuccess = lazy(
  () => import("src/pages/whatsapp-onboarding/success"),
);

// Email Domain & Sender Management Pages
const EmailDomainsPage = lazy(
  () => import("src/pages/email/domains"),
);
const EmailDomainNewPage = lazy(
  () => import("src/pages/email/domains/new"),
);
const EmailDomainDetailPage = lazy(
  () => import("src/pages/email/domains/[id]"),
);
const EmailDomainSetupPage = lazy(
  () => import("src/pages/email/domains/[id]/setup"),
);
const EmailSenderNewPage = lazy(
  () => import("src/pages/email/domains/[id]/senders/new"),
);
const EmailSenderVerifyPage = lazy(
  () => import("src/pages/email/senders/verify"),
);


// Messenger Onboarding Pages
const MessengerOnboardingSuccess = lazy(
  () => import("src/pages/messenger-onboarding/success"),
);
const MessengerOnboardingFail = lazy(
  () => import("src/pages/messenger-onboarding/fail"),
);
const MessengerOnboardingFlow = lazy(
  () => import("src/pages/messenger-onboarding"),
);

// Instagram Onboarding Pages
const InstagramOnboardingFlow = lazy(
  () => import("src/pages/instagram-onboarding"),
);
const InstagramOnboardingSuccess = lazy(
  () => import("src/pages/instagram-onboarding/success"),
);
const InstagramOnboardingFail = lazy(
  () => import("src/pages/instagram-onboarding/fail"),
);
// @deprecated — Standalone email pages removed in favour of unified Conversations UI
// const EmailComposePage = lazy(() => import("src/pages/email/compose"));
// const EmailInboxPage = lazy(() => import("src/pages/email/inbox"));
// const EmailInboxConversationPage = lazy(() => import("src/pages/email/inbox/[id]"));
// const EmailMessagesPage = lazy(() => import("src/pages/email/messages"));
// const EmailMessageDetailPage = lazy(() => import("src/pages/email/messages/[id]"));
const EmbeddedSignupPage = lazy(
  () => import("src/pages/embedded-signup"),
);
const EmbeddedSignupSuccess = lazy(
  () => import("src/pages/embedded-signup/success"),
);
const EmbeddedSignupFail = lazy(
  () => import("src/pages/embedded-signup/fail"),
);
const EmbeddedSignupAttempt = lazy(
  () => import("src/pages/embedded-signup/attempt/attemptId"),
);

const CalendarPage = lazy(() => import("src/pages/calendar/CalendarPage"));
const CreateEventTypePage = lazy(() => import("src/pages/calendar/CreateEventTypePage"));
const EditEventTypePage = lazy(() => import("src/pages/calendar/EditEventTypePage"));
const BookingsListPage = lazy(() => import("src/pages/calendar/BookingsListPage"));
const CartLandingPage = lazy(() => import("src/pages/cart/CartLandingPage"));

const privateRoutes = [
  {
    path: "",
    Component: CavePage,
    noFooterRoute: false,
  },
  {
    path: "account/settings",
    Component: AccountSettings,
    noFooterRoute: false,
  },
  {
    path: "account/billing",
    Component: BillingPage,
    noFooterRoute: false,
    permission: { action: "read", subject: "billing" },
  },
  {
    path: "account/payment-processing",
    Component: PaymentProcessingPage,
    noFooterRoute: false,
  },
  {
    path: "billing/overage-consumption",
    Component: OverageConsumptionPage,
    noFooterRoute: false,
  },
  {
    path: "workflows/studio/:id",
    Component: StudioPage,
    noFooterRoute: true,
  },
  {
    path: "workflows/canvas/:id",
    Component: ShadowCanvasPage,
    noFooterRoute: true,
  },
  {
    path: "workflows",
    Component: WorkflowsListPage,
    noFooterRoute: false,
    permission: { action: "read", subject: "workflow" },
  },
  {
    path: "workflows/create",
    Component: CreateWorkflowPage,
    noFooterRoute: false,
  },
  {
    path: "channels/create",
    Component: AddChannel,
    noFooterRoute: false,
  },
  {
    path: "channels/update/:id",
    Component: UpdateChannel,
    noFooterRoute: false,
  },
  {
    path: "channels/webchat/preview",
    Component: WebchatPreviewPage,
    noFooterRoute: true,
  },
  {
    path: "analytics",
    Component: DashboardPage,
    noFooterRoute: false,
  },
  {
    path: "conversations",
    Component: ConversationsPage,
    noFooterRoute: true, // Full-screen inbox experience
    permission: { action: "read", subject: "conversation" },
  },
  {
    path: "conversations/:id",
    Component: ConversationDetailPage,
    noFooterRoute: true, // Full-screen inbox experience
  },
  {
    path: "tickets",
    Component: TicketsPage,
    noFooterRoute: false,
    permission: { action: "read", subject: "ticket" },
  },
  {
    path: "tickets/:id",
    Component: TicketDetailPage,
    noFooterRoute: false,
    permission: { action: "read", subject: "ticket" },
  },
  {
    path: "campaigns",
    Component: CampaignsPage,
    noFooterRoute: false,
    permission: { action: "read", subject: "campaign" },
  },
  {
    path: "campaigns/create",
    Component: CreateCampaignPage,
    noFooterRoute: false,
  },
  {
    path: "campaigns/:id/edit",
    Component: EditCampaignPage,
    noFooterRoute: false,
  },
  {
    path: "campaigns/:id",
    Component: CampaignDetailPage,
    noFooterRoute: false,
  },
  {
    path: "templates",
    Component: TemplatesPage,
    noFooterRoute: false,
    permission: { action: "read", subject: "workflow" }, // Templates use workflow permission
  },
  {
    path: "templates/create",
    Component: CreateTemplatePage,
    noFooterRoute: false,
  },
  {
    path: "templates/:id",
    Component: TemplateDetailPage,
    noFooterRoute: false,
  },
  {
    path: "templates/:id/edit",
    Component: TemplateEditPage,
    noFooterRoute: false,
  },
  {
    path: "chatbots",
    Component: ChatbotsPage,
    noFooterRoute: false,
  },
  {
    path: "chatbots/create",
    Component: CreateChatbotPage,
    noFooterRoute: false,
  },
  {
    path: "chatbots/:id",
    Component: ChatbotViewPage,
    noFooterRoute: false,
  },
  {
    path: "chatbots/:id/edit",
    Component: EditChatbotPage,
    noFooterRoute: false,
  },
  {
    path: "social-listening",
    Component: SocialListeningPage,
    noFooterRoute: false,
  },
  // Backward compatibility: redirect /cdp to /customers/people
  {
    path: "cdp",
    Component: CDPPage,
    noFooterRoute: false,
  },
  {
    path: "cdp/profile/:id",
    Component: ProfileDetailsPage,
    noFooterRoute: false,
  },
  {
    path: "roles/create",
    Component: AddRole,
    noFooterRoute: false,
  },
  {
    path: "roles",
    Component: RolesListPage,
    noFooterRoute: true,
  },
  {
    path: "users",
    Component: UserListPage,
    noFooterRoute: true,
  },
  {
    path: "channels",
    Component: Channels,
    noFooterRoute: false,
    permission: { action: "read", subject: "channel" },
  },
  {
    path: "channels/:id",
    Component: ChannelDetailPage,
    noFooterRoute: false,
  },
  {
    path: "sender-registrations",
    Component: SenderRegistrationsPage,
    noFooterRoute: false,
  },
  {
    path: "sender-registrations/whatsapp/register",
    Component: WhatsAppRegisterPage,
    noFooterRoute: false,
  },
  {
    path: "sender-registrations/sms/register",
    Component: SMSRegisterPage,
    noFooterRoute: false,
  },
  {
    path: "sender-registrations/whatsapp/details/:id",
    Component: WhatsAppDetailsPage,
    noFooterRoute: false,
  },
  {
    path: "sender-registrations/sms/details/:id",
    Component: SMSDetailsPage,
    noFooterRoute: false,
  },
  {
    path: "meta-partner",
    Component: MetaPartnerListPage,
    noFooterRoute: false,
  },
  {
    path: "meta-partner/onboarding",
    Component: MetaPartnerOnboardingPage,
    noFooterRoute: false,
  },
  {
    path: "meta-partner/onboarding/:id",
    Component: MetaPartnerOnboardingPage,
    noFooterRoute: false,
  },
  {
    path: "contact-center",
    Component: ContactCenterPage,
    noFooterRoute: false,
  },
  {
    path: "contact-center/setup",
    Component: ContactCenterSetup,
    noFooterRoute: false,
  },
  {
    path: "call-routing",
    Component: CallRoutingPage,
    noFooterRoute: false,
  },
  {
    path: "call-recording",
    Component: CallRecordingPage,
    noFooterRoute: false,
  },
  {
    path: "logs",
    Component: LogsPage,
    noFooterRoute: false,
  },
  {
    path: "files",
    Component: FilesPage,
    noFooterRoute: false,
  },
  {
    path: "integrations",
    Component: IntegrationsPage,
    noFooterRoute: false,
  },
  {
    path: "stripe-status",
    Component: StripeTestPage,
    noFooterRoute: false,
  },
  {
    path: "settings/queue-management",
    Component: ContactCenterSetup,
    noFooterRoute: false,
    permission: { action: "manage", subject: "organization" },
  },
  // Legacy routes - redirect to contact center setup
  {
    path: "settings/queues",
    Component: ContactCenterSetup,
    noFooterRoute: false,
    permission: { action: "manage", subject: "organization" },
  },
  {
    path: "settings/agent-allocation",
    Component: ContactCenterSetup,
    noFooterRoute: false,
    permission: { action: "manage", subject: "organization" },
  },
  // Support Center / Help Center routes
  {
    path: "support-center",
    Component: HelpCenterPage,
    noFooterRoute: false,
  },
  {
    path: "support-center/help",
    Component: HelpCenterPage,
    noFooterRoute: false,
  },
  {
    path: "support-center/tickets",
    Component: SupportTicketsPage,
    noFooterRoute: false,
  },
  {
    path: "support-center/tickets/new",
    Component: NewSupportTicketPage,
    noFooterRoute: false,
  },
  {
    path: "support-center/tickets/:id",
    Component: SupportTicketDetailPage,
    noFooterRoute: false,
  },
  // Aliases for convenience
  {
    path: "help",
    Component: HelpCenterPage,
    noFooterRoute: false,
  },
  {
    path: "help-center",
    Component: HelpCenterPage,
    noFooterRoute: false,
  },
  {
    path: "whatsapp-onboarding",
    Component: WhatsAppOnboardingList,
    noFooterRoute: false,
    permission: { action: "read", subject: "whatsapp-onboarding" },
  },
  {
    path: "whatsapp-onboarding/add",
    Component: WhatsAppOnboardingFlow,
    noFooterRoute: false,
    permission: { action: "create", subject: "whatsapp-onboarding" },
  },
  {
    path: "whatsapp-onboarding/:id",
    Component: WhatsAppOnboardingDetail,
    noFooterRoute: false,
    permission: { action: "read", subject: "whatsapp-onboarding" },
  },
  {
    path: "whatsapp-onboarding/update/:id",
    Component: WhatsAppOnboardingFlow,
    noFooterRoute: false,
    permission: { action: "update", subject: "whatsapp-onboarding" },
  },
  {
    path: "whatsapp-onboarding/resubmit/:id",
    Component: WhatsAppOnboardingFlow,
    noFooterRoute: false,
    permission: { action: "create", subject: "whatsapp-onboarding" },
  },
  {
    path: "whatsapp-onboarding/success",
    Component: WhatsAppOnboardingSuccess,
    noFooterRoute: false,
    permission: { action: "read", subject: "whatsapp-onboarding" },
  },

  // ── Email Domain & Sender Management ─────────────────────────────────
  {
    path: "email/domains",
    Component: EmailDomainsPage,
    noFooterRoute: false,
  },
  {
    path: "email/domains/new",
    Component: EmailDomainNewPage,
    noFooterRoute: false,
  },
  {
    path: "email/domains/:id",
    Component: EmailDomainDetailPage,
    noFooterRoute: false,
  },
  {
    path: "email/domains/:id/setup",
    Component: EmailDomainSetupPage,
    noFooterRoute: false,
  },
  {
    path: "email/domains/:id/senders/new",
    Component: EmailSenderNewPage,
    noFooterRoute: false,
  },
  // @deprecated — Standalone email routes removed; use unified Conversations UI
  // { path: "email/compose",    Component: EmailComposePage,           noFooterRoute: false },
  // { path: "email/inbox",      Component: EmailInboxPage,             noFooterRoute: false },
  // { path: "email/inbox/:id",  Component: EmailInboxConversationPage, noFooterRoute: false },
  // { path: "email/messages",   Component: EmailMessagesPage,          noFooterRoute: false },
  // { path: "email/messages/:id", Component: EmailMessageDetailPage,   noFooterRoute: false },
  {
    path: "embedded-signup",
    Component: EmbeddedSignupPage,
    noFooterRoute: false,
  },
  {
    path: "embedded-signup/attempt/:attemptId",
    Component: EmbeddedSignupAttempt,
    noFooterRoute: false,
  },
  {
    path: "embedded-signup/success",
    Component: EmbeddedSignupSuccess,
    noFooterRoute: false,
  },
  {
    path: "embedded-signup/fail",
    Component: EmbeddedSignupFail,
    noFooterRoute: false,
  },
  {
    path: "messenger-onboarding",
    Component: MessengerOnboardingFlow,
    noFooterRoute: false,
  },
  {
    path: "messenger-onboarding/success",
    Component: MessengerOnboardingSuccess,
    noFooterRoute: false,
  },
  {
    path: "messenger-onboarding/fail",
    Component: MessengerOnboardingFail,
    noFooterRoute: false,
  },
  {
    path: "instagram-onboarding",
    Component: InstagramOnboardingFlow,
    noFooterRoute: false,
  },
  {
    path: "instagram-onboarding/success",
    Component: InstagramOnboardingSuccess,
    noFooterRoute: false,
  },
  {
    path: "instagram-onboarding/fail",
    Component: InstagramOnboardingFail,
    noFooterRoute: false,
  },
  { path: "calendar",                          Component: CalendarPage,         noFooterRoute: false },
  { path: "calendar/event-types/new",          Component: CreateEventTypePage,  noFooterRoute: false },
  { path: "calendar/event-types/:id",          Component: EditEventTypePage,    noFooterRoute: false },
  { path: "calendar/bookings",                 Component: BookingsListPage,     noFooterRoute: false },
  { path: "cart",                              Component: CartLandingPage,      noFooterRoute: false },
];

// Separate onboarding routes that don't use the main layout
const onboardingRoutes = [
  {
    path: "onboarding",
    Component: OnboardingFlow,
  },
  {
    path: "onboarding/payment-status",
    Component: PaymentStatusPage,
  },
  {
    path: "onboarding/enterprise-processing",
    Component: EnterpriseProcessingPage,
  },
  {
    path: "accounts/new",
    Component: OnboardingFlow,
  },
  {
    path: "accounts/select",
    Component: AccountSelector,
  },
];

// Customers (CDP) routes — rendered inside CustomersLayout which mounts the AI Copilot panel.
// Paths here are relative to the "customers" parent route.
export const customersRoutes = [
  {
    path: "dashboard",
    Component: CustomerDashboardPage,
    permission: { action: "read", subject: "cdp" },
  },
  {
    path: "people",
    Component: PeoplePage,
    permission: { action: "read", subject: "cdp" },
  },
  {
    path: "segments",
    Component: SegmentsPage,
    permission: { action: "read", subject: "cdp" },
  },
  {
    path: "events",
    Component: EventsPage,
    permission: { action: "read", subject: "cdp" },
  },
  {
    path: "labels",
    Component: LabelsPage,
    permission: { action: "read", subject: "cdp" },
  },
  {
    path: "consent",
    Component: ConsentPage,
    permission: { action: "read", subject: "cdp" },
  },
  {
    path: "rules",
    Component: RulesPage,
    permission: { action: "read", subject: "cdp" },
  },
  {
    path: "rules/:id",
    Component: RuleDetailPage,
    permission: { action: "read", subject: "cdp" },
  },
  {
    path: "definitions",
    Component: DefinitionsPage,
    permission: { action: "read", subject: "cdp" },
  },
  {
    path: "goals",
    Component: GoalsPage,
    permission: { action: "read", subject: "cdp" },
  },
  {
    path: "properties",
    Component: PropertiesPage,
    permission: { action: "read", subject: "cdp" },
  },
  {
    path: "personas",
    Component: PersonasPage,
    permission: { action: "read", subject: "cdp" },
  },
  {
    path: "lists",
    Component: ListsPage,
    permission: { action: "read", subject: "cdp" },
  },
  {
    path: "actions",
    Component: AiActionsPage,
    permission: { action: "read", subject: "cdp" },
  },
  {
    path: "scoring",
    Component: ScoringPage,
    permission: { action: "read", subject: "cdp" },
  },
];

// Update routes export
export const routes = {
  private: privateRoutes,
  onboarding: onboardingRoutes,
};
